import {
  Module,
  VuexModule,
  Mutation,
  getModule,
} from "vuex-module-decorators";

const name = "language";

import store from "@/store";
// Imported from the leaf module, never from "@/i18n/Languages" or "@/plugins/vuetify": the
// property initializers below run while those modules are still evaluating, so their exports are
// undefined at that moment. See the note in LanguageTypes.ts.
import {
  DEFAULT_LOCALE,
  isSupportedLanguage,
  Language,
  parseStoredLocale,
} from "@/i18n/LanguageTypes";

const LOCALE_STORAGE_KEY = "locale";

/**
 * Reads the persisted locale and drops the stored value when it cannot be used.
 *
 * Repairing here is what recovers a browser that already holds a bad value. The parse below used
 * to run unguarded on whatever was in storage, so a single bad write ("undefined", from
 * `JSON.stringify(undefined)`) threw in the property initializer and crashed every subsequent app
 * start until the key was removed by hand.
 */
function readPersistedLocale(): string | null {
  const raw = localStorage.getItem(LOCALE_STORAGE_KEY);

  if (raw !== null && parseStoredLocale(raw) === null) {
    localStorage.removeItem(LOCALE_STORAGE_KEY);
    return null;
  }

  return raw;
}

if (store.state[name]) {
  store.unregisterModule(name);
}

@Module({ dynamic: true, namespaced: true, store, name: name })
export class LanguageVuexModule extends VuexModule {
  localeCacheStringified = readPersistedLocale();
  localeCache = parseStoredLocale(this.localeCacheStringified) ?? DEFAULT_LOCALE;

  @Mutation
  setLocale(locale: Language) {
    // Guarded at the write, not only at the callers: `JSON.stringify` of an unsupported value
    // returns `undefined`, which `setItem` stores as the text "undefined" and which no later read
    // can turn back into a locale.
    if (!isSupportedLanguage(locale)) {
      return;
    }

    this.localeCache = locale;
    localStorage.setItem(LOCALE_STORAGE_KEY, JSON.stringify(locale));
  }

  @Mutation
  clear() {
    this.localeCacheStringified = null;
    this.localeCache = DEFAULT_LOCALE;

    localStorage.removeItem(LOCALE_STORAGE_KEY);
  }
}

export const LanguageModule = getModule(LanguageVuexModule);
